import { createStore, applyMiddleware, compose } from "redux";
import createSagaMiddleware from "redux-saga";
import moment from "moment";
import cogoToast from "../components/Toast";
import { getPersistConfig } from "redux-deep-persist";
import { persistStore, persistReducer } from "redux-persist";
import { encryptTransform } from "redux-persist-transform-encrypt";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { blacklist } from "./blacklist";
import reducers from "./reducers";
import rootSaga from "./sagas";
import axios from "axios";

const sagaMiddleware = createSagaMiddleware();
const persistSecretKey = process.env.REACT_APP_REDUX_PERSIST_SECRET_KEY;
const transforms = persistSecretKey
  ? [
      encryptTransform({
        secretKey: persistSecretKey,
        onError: err => {
          console.error(err);
        },
      }),
    ]
  : [];

const persistConfig = getPersistConfig({
  key: "root",
  storage,
  transforms,
  stateReconciler: autoMergeLevel2,
  blacklist: blacklist,
  rootReducer: reducers,
});
const globalReducer = (state, action) => {
  if (action.type === "USER_SIGNED_OUT") {
    state = undefined;
  };

  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, globalReducer);

function isTokenExpired(state) {
  const { accessToken, expTime } = state.auth;
  let expired = true;
  if (moment(expTime).isValid()) {
    expired = moment() > moment(expTime);
  }
  return accessToken && expired;
}

const checkTokenExpirationMiddleware = store => next => action => {
  // this middleware will run pretty much on anything that accesses the store
  // and will check if token is EXPIRED. If it is, will automatically log user out.
  // It will also clean out redux state (memory) and clear redux persist (local storage)
  const state = store.getState();

  if (state.auth.accessToken) {
    next(action);
    if (isTokenExpired(state)) {
      cogoToast.error("Your token has expired, please login again");
      store.dispatch({ type: "USER_SIGNED_OUT" });
      localStorage.clear();
      window.eventEmitter.emit("invalid_token");
    }
  } else {
    next(action);
  }
};

export default function configureStore(initialState = {}) {
  // Create the store with two middlewares
  // x. sagaMiddleware: Makes redux-sagas work
  // x. checks token expiry on each action (so on refreshing browser, or re-opening browser or really any redux action)
  const middlewares = [sagaMiddleware, checkTokenExpirationMiddleware];

  const enhancers = [applyMiddleware(...middlewares)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== "production" &&
    typeof window === "object" &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          shouldHotReload: false, // eslint-disable-line
        }) // eslint-disable-line
      : compose;
  /* eslint-enable */

  const store = createStore(
    persistedReducer,
    initialState,
    composeEnhancers(...enhancers)
  );

  // Extensions

  const persistor = persistStore(store);

  store.runSaga = sagaMiddleware.run(rootSaga);

  return { persistor, store };
}

const { persistor, store } = configureStore({});

export {
  persistor,
  store
}
